import { useEffect, useState } from "react";
import { Zap, Wand2, Trash2, ShieldAlert, Clock, KeyRound, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAiEntitlement } from "@/hooks/useAiEntitlement";
import { AiUpgradeCard } from "@/components/merchant/AiUpgrade";

/**
 * Merchant → AI Integrations (Cyannect MCP Connector, iter77).
 *
 * When FEATURE_MCP_CONNECTOR is off, /api/mcp/* returns 404 uniformly and this
 * page shows a friendly "coming soon" placeholder.
 */
export default function AiIntegrations() {
  const [clients, setClients] = useState([]);
  const [connections, setConnections] = useState([]);
  const [available, setAvailable] = useState(true);
  const { loading: entLoading, aiEnabled, mcpAvailable } = useAiEntitlement();

  const load = async () => {
    try {
      const [c, conns] = await Promise.all([
        api.get("/mcp/clients"),
        api.get("/mcp/connections"),
      ]);
      setClients(c.data.items || []);
      setConnections(conns.data.items || []);
      setAvailable(true);
    } catch (e) {
      if (e.response?.status === 404) setAvailable(false);
    }
  };

  useEffect(() => {
    if (entLoading || !aiEnabled) return;
    if (mcpAvailable) load(); else setAvailable(false);
  }, [entLoading, aiEnabled, mcpAvailable]);

  const revoke = async (cid, name) => {
    if (!window.confirm(`Revoke access for ${name}? The AI will lose all connection immediately.`)) return;
    try {
      await api.delete(`/mcp/connections/${cid}`, { data: { reason: "merchant" } });
      toast.success(`${name} disconnected`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || "Revoke failed");
    }
  };

  if (!available) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center" data-testid="mcp-disabled">
        <div className="pf-card p-10">
          <Wand2 className="w-12 h-12 mx-auto text-cyan-600 mb-3" />
          <h2 className="font-display text-3xl font-semibold text-slate-900">AI Integrations</h2>
          <p className="text-slate-600 mt-2">
            Coming soon — connect Claude, ChatGPT or Gemini to prepare Cyannect coupon
            campaigns directly from your AI assistant. In private beta.
          </p>
        </div>
      </div>
    );
  }

  const connectionsByClient = Object.fromEntries(
    connections.filter((c) => c.status === "active")
               .map((c) => [c.client_id, c])
  );

  return (
    <div data-testid="ai-integrations">
      <div className="pf-chip pf-chip-cyan w-fit"><Zap className="w-3 h-3" /> Integrations</div>
      <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight">AI Integrations</h2>
      <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
        Use your preferred compatible AI assistant to prepare Cyannect coupon
        campaigns securely. You review and publish every draft — the AI never
        publishes on your behalf.
      </p>
      {!entLoading && !aiEnabled && (
        <div className="mt-5">
          <AiUpgradeCard feature="Connecting AI assistants (MCP connectors)" testid="mcp-locked" />
        </div>
      )}

      {connections.filter((c) => c.status === "active").length > 0 && (
        <div className="mt-8">
          <h3 className="font-semibold text-slate-900 mb-3">Connected</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {connections.filter((c) => c.status === "active").map((c) => (
              <div key={c.id} className="pf-card p-5" data-testid={`connection-card-${c.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-semibold text-slate-900 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      {c.client_display_name}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 flex items-center gap-3">
                      <span><Clock className="w-3 h-3 inline mr-1" />
                        Connected {new Date(c.created_at).toLocaleDateString()}</span>
                      {c.last_used_at && (
                        <span>Last used {new Date(c.last_used_at).toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                  <button onClick={() => revoke(c.id, c.client_display_name)}
                          className="p-1.5 rounded hover:bg-rose-100 text-rose-600"
                          data-testid={`revoke-${c.id}`}
                          title="Revoke access">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="mt-3 flex flex-wrap gap-1">
                  {(c.scopes || []).map((s) => (
                    <span key={s} className="pf-chip pf-chip-lav text-[10px] font-mono">{s}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-10">
        <h3 className="font-semibold text-slate-900 mb-3">Available AI clients</h3>
        <div className="grid md:grid-cols-3 gap-4">
          {clients.map((c) => {
            const connected = connectionsByClient[c.id];
            const compat = c.compatibility_status || "not_tested";
            return (
              <div key={c.id} className="pf-card p-5 relative" data-testid={`client-card-${c.id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-slate-900">{c.name}</div>
                  <span className={`pf-chip text-[10px] ${
                    compat === "supported" ? "pf-chip-mint" :
                    compat === "beta" ? "pf-chip-cyan" :
                    "pf-chip-lav"
                  }`}>
                    {compat === "supported" ? "Supported" :
                     compat === "beta" ? "Beta" :
                     compat === "testing" ? "Testing" :
                     compat === "partially_supported" ? "Partial" :
                     "Not tested"}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {connected ? "Already connected." : "Ready to connect via OAuth."}
                </p>
                <div className="mt-4">
                  {connected ? (
                    <span className="text-xs text-emerald-700 font-semibold flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Active connection
                    </span>
                  ) : (
                    <button className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1"
                            data-testid={`connect-${c.id}`}
                            disabled={compat === "coming_soon"}
                            onClick={() => toast("Setup instructions coming from your AI client — cyannect.co/docs/mcp for details.")}>
                      <KeyRound className="w-3 h-3" />
                      {compat === "coming_soon" ? "Coming soon" : "Connect"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-10 rounded-2xl bg-amber-50/60 border border-amber-200 p-4 max-w-2xl">
        <div className="flex items-start gap-2">
          <ShieldAlert className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-slate-700">
            <b>Only authorize AI clients you trust.</b> Connections are scoped, expire hourly,
            and can be revoked instantly. No AI is ever granted permission to publish coupons —
            you always review and publish yourself.
          </div>
        </div>
      </div>
    </div>
  );
}
